'use client';

import { useTranslations } from 'next-intl';
import { type FC, useCallback } from 'react';

import { useMutation } from '@apollo/client';

import omit from 'lodash/omit';

import {
  RegistrationForm,
  type RegistrationFormData,
} from '@/app/[locale]/(not-authorized)/registration/components/Registration/components/RegistrationForm';
import { ButtonLink } from '@/components/common/button/ButtonLink';
import { Flex } from '@/components/common/Flex';
import { Text } from '@/components/common/Text';
import { urls } from '@/constants/urls';
import { useBoolean } from '@/hooks/use-boolean';
import { useCommonApolloOptions } from '@/hooks/use-common-apollo-options';

import { gql } from '../../../../../../../generated';

import styles from './Registration.module.scss';

const REGISTRATION = gql(/* GraphQL */ `
  mutation Registration($data: UsersRegistrationInput!) {
    usersRegistration(data: $data) {
      id
    }
  }
`);

export const Registration: FC = () => {
  const t = useTranslations('pages.registration.form');

  const { value: isSuccess, setTrue: success } = useBoolean();

  const apolloOptions = useCommonApolloOptions();

  const [registration] = useMutation(REGISTRATION, {
    ...apolloOptions,
    context: {
      noAuth: true,
    },
    onCompleted: success,
  });

  const handleSubmit = useCallback(
    async (data: RegistrationFormData) => {
      await registration({
        variables: {
          data: {
            ...omit(data, ['passwordRepeat', 'payments_currencies']),
            payments_currencies: data.payments_currencies.map(({ id }) => id),
            telegram: data.telegram.trim().replace(/^@/, '') || undefined,
          },
        },
      });
    },
    [registration]
  );

  return isSuccess ? (
    <Flex className={styles.successBlock} direction="column" alignItems="center" justifyContent="center" gap={9}>
      <Flex direction="column" alignItems="center" gap={4}>
        <Text className={styles.successTitle} align="center">
          {t('successBlock.title')}
        </Text>

        <Text type="medium18" align="center" color="white-50p">
          {t('successBlock.subtitle')}
        </Text>
      </Flex>

      <ButtonLink className={styles.homeButton} href={urls.MarketingSiteHome} isOutsideLink>
        {t('successBlock.homeButton')}
      </ButtonLink>
    </Flex>
  ) : (
    <RegistrationForm onSubmit={handleSubmit} />
  );
};
